import {
  getLeoUuid,
  LeoUUID,
  getObject,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { QuestionDetails, QuestionDetailsDTO } from "../types/questionDetails";
import { SurveyDetailsAndQuestions, SurveyDetailsAndQuestionsDTO } from "../types/surveyDetailsAndQuestions";
import { Code, CodeDTO } from "../types/code";
/* eslint-disable import/export */
export abstract class UpdateSurveyQuestionRPC implements
  LeoRPC<UpdateSurveyQuestionRPC.Request, UpdateSurveyQuestionRPC.Response, UpdateSurveyQuestionRPC.Errors.Errors>
{
  abstract execute(
    request: UpdateSurveyQuestionRPC.Request
  ): Promise<LeoRPCResult<UpdateSurveyQuestionRPC.Response, UpdateSurveyQuestionRPC.Errors.Errors>>;
}

export namespace UpdateSurveyQuestionRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    surveyId: string;
    questionId: string;
    questionDetails: QuestionDetailsDTO;
    projectId: string;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isUpdateSurveyQuestionRPCRequest: boolean = true;
  
    readonly surveyId: LeoUUID;
    readonly questionId: LeoUUID;
    readonly questionDetails: QuestionDetails;
    readonly projectId: LeoUUID;
  
  
    constructor(
    surveyId: LeoUUID, 
    questionId: LeoUUID, 
    questionDetails: QuestionDetails, 
    projectId: LeoUUID
    ) {
      this.surveyId = surveyId;
      this.questionId = questionId;
      this.questionDetails = questionDetails;
      this.projectId = projectId;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const surveyId = getLeoUuid(dto, "surveyId", InvalidRequestSurveyIdError);
      const questionId = getLeoUuid(dto, "questionId", InvalidRequestQuestionIdError);
      const questionDetails = QuestionDetails.fromDTO(getObject(dto, "questionDetails", InvalidRequestQuestionDetailsError));
      const projectId = getLeoUuid(dto, "projectId", InvalidRequestProjectIdError);
      return new Request(
        surveyId,
        questionId,
        questionDetails,
        projectId
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          surveyId: this.surveyId.toDTO(),
          questionId: this.questionId.toDTO(),
          questionDetails: this.questionDetails.toDTO(),
          projectId: this.projectId.toDTO()
      };
      return dto;
    }
    }
  
  Request.prototype.toString = function toString(): string {
    return `UpdateSurveyQuestionRPC(surveyId='${this.surveyId}',questionId='${this.questionId}',questionDetails='${this.questionDetails}',projectId='${this.projectId}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {
    surveyDetailsAndQuestions: SurveyDetailsAndQuestionsDTO;
  }
  
  export class Response implements LeoRPCResponse {
    readonly isUpdateSurveyQuestionResponse: boolean = true;
  
    readonly surveyDetailsAndQuestions: SurveyDetailsAndQuestions;
  
  
    constructor(
    surveyDetailsAndQuestions: SurveyDetailsAndQuestions
    ) {
      this.surveyDetailsAndQuestions = surveyDetailsAndQuestions;
    }
  
    static fromDTO(dto: ResponseDTO): Response {
      const surveyDetailsAndQuestions = SurveyDetailsAndQuestions.fromDTO(getObject(dto, "surveyDetailsAndQuestions", InvalidResponseSurveyDetailsAndQuestionsError));
      return new Response(
        surveyDetailsAndQuestions
      );
    }
  
    toDTO(): ResponseDTO {
      const dto: ResponseDTO = {
          surveyDetailsAndQuestions: this.surveyDetailsAndQuestions.toDTO()
      };
      return dto;
    }
    }
  
  Response.prototype.toString = function toString(): string {
    return `UpdateSurveyQuestion(surveyDetailsAndQuestions='${this.surveyDetailsAndQuestions}')`;
  };
  

  export enum RPCError {
    InvalidSurveyId = "INVALID_SURVEY_ID",
    InvalidProjectId = "INVALID_PROJECT_ID",
    InvalidQuestionId = "INVALID_QUESTION_ID",
    QuestionWasDeleted = "QUESTION_WAS_DELETED",
    SurveyIsClosed = "SURVEY_IS_CLOSED",
    ProjectAlreadyArchived = "PROJECT_ALREADY_ARCHIVED",
    QuestionCodeNotUnique = "QUESTION_CODE_NOT_UNIQUE",
    OptionCodeNotUnique = "OPTION_CODE_NOT_UNIQUE",
    JsonVersionNotSupported = "JSON_VERSION_NOT_SUPPORTED",
    InvalidQuestionDetailsJson = "INVALID_QUESTION_DETAILS_JSON",
    InvalidChildQuestionType = "INVALID_CHILD_QUESTION_TYPE",
    InvalidMaxSelection = "INVALID_MAX_SELECTION"
  }

  export namespace Errors {
    
    export interface InvalidSurveyIdDTO {
      code: string;
    }
    
    export class InvalidSurveyId extends LeoRPCError<RPCError> {
      readonly isInvalidSurveyId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidSurveyId;
        super(code);
      }
    
    
      toDTO(): InvalidSurveyIdDTO {
        const dto: InvalidSurveyIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidSurveyId.prototype.toString = function toString(): string {
      return `UpdateSurveyQuestionRPC.INVALID_SURVEY_ID()`;
    };
    
    
    export interface InvalidProjectIdDTO {
      code: string;
    }
    
    export class InvalidProjectId extends LeoRPCError<RPCError> {
      readonly isInvalidProjectId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidProjectId;
        super(code);
      }
    
    
      toDTO(): InvalidProjectIdDTO {
        const dto: InvalidProjectIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidProjectId.prototype.toString = function toString(): string {
      return `UpdateSurveyQuestionRPC.INVALID_PROJECT_ID()`;
    };
    
    
    export interface InvalidQuestionIdDTO {
      code: string;
    }
    
    export class InvalidQuestionId extends LeoRPCError<RPCError> {
      readonly isInvalidQuestionId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidQuestionId;
        super(code);
      }
    
    
      toDTO(): InvalidQuestionIdDTO {
        const dto: InvalidQuestionIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidQuestionId.prototype.toString = function toString(): string {
      return `UpdateSurveyQuestionRPC.INVALID_QUESTION_ID()`;
    };
    
    
    export interface QuestionWasDeletedDTO {
      code: string;
    }
    
    export class QuestionWasDeleted extends LeoRPCError<RPCError> {
      readonly isQuestionWasDeleted: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.QuestionWasDeleted;
        super(code);
      }
    
    
      toDTO(): QuestionWasDeletedDTO {
        const dto: QuestionWasDeletedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    QuestionWasDeleted.prototype.toString = function toString(): string {
      return `UpdateSurveyQuestionRPC.QUESTION_WAS_DELETED()`;
    };
    
    
    export interface SurveyIsClosedDTO {
      code: string;
    }
    
    export class SurveyIsClosed extends LeoRPCError<RPCError> {
      readonly isSurveyIsClosed: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.SurveyIsClosed;
        super(code);
      }
    
    
      toDTO(): SurveyIsClosedDTO {
        const dto: SurveyIsClosedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    SurveyIsClosed.prototype.toString = function toString(): string {
      return `UpdateSurveyQuestionRPC.SURVEY_IS_CLOSED()`;
    };
    
    
    export interface ProjectAlreadyArchivedDTO {
      code: string;
    }
    
    export class ProjectAlreadyArchived extends LeoRPCError<RPCError> {
      readonly isProjectAlreadyArchived: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.ProjectAlreadyArchived;
        super(code);
      }
    
    
      toDTO(): ProjectAlreadyArchivedDTO {
        const dto: ProjectAlreadyArchivedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    ProjectAlreadyArchived.prototype.toString = function toString(): string {
      return `UpdateSurveyQuestionRPC.PROJECT_ALREADY_ARCHIVED()`;
    };
    
    
    export interface QuestionCodeNotUniqueDTO {
      code: string;
    }
    
    export class QuestionCodeNotUnique extends LeoRPCError<RPCError> {
      readonly isQuestionCodeNotUnique: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.QuestionCodeNotUnique;
        super(code);
      }
    
    
      toDTO(): QuestionCodeNotUniqueDTO {
        const dto: QuestionCodeNotUniqueDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    QuestionCodeNotUnique.prototype.toString = function toString(): string {
      return `UpdateSurveyQuestionRPC.QUESTION_CODE_NOT_UNIQUE()`;
    };
    
    
    export interface OptionCodeNotUniqueDTO {
      code: string;
      optionCode: CodeDTO;
    }
    
    export class OptionCodeNotUnique extends LeoRPCError<RPCError> {
      readonly isOptionCodeNotUnique: boolean = true;
    
      readonly optionCode: Code;
    
      constructor(
      optionCode: Code
      ) {
        const code = RPCError.OptionCodeNotUnique;
        super(code);
        this.optionCode = optionCode;
      }
    
      static fromDTO(dto: OptionCodeNotUniqueDTO): OptionCodeNotUnique {
        const optionCode = Code.fromDTO(getObject(dto, "optionCode", InvalidResponseOptionCodeNotUniqueOptionCodeError));
        return new OptionCodeNotUnique(
          optionCode
        );
      }
    
      toDTO(): OptionCodeNotUniqueDTO {
        const dto: OptionCodeNotUniqueDTO = {
          code: this.code,
          optionCode: this.optionCode.toDTO()
        };
        return dto;
      }
    
      }
    
    OptionCodeNotUnique.prototype.toString = function toString(): string {
      return `UpdateSurveyQuestionRPC.OPTION_CODE_NOT_UNIQUE(optionCode='${this.optionCode}')`;
    };
    
    
    export interface JsonVersionNotSupportedDTO {
      code: string;
    }
    
    export class JsonVersionNotSupported extends LeoRPCError<RPCError> {
      readonly isJsonVersionNotSupported: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.JsonVersionNotSupported;
        super(code);
      }
    
    
      toDTO(): JsonVersionNotSupportedDTO {
        const dto: JsonVersionNotSupportedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    JsonVersionNotSupported.prototype.toString = function toString(): string {
      return `UpdateSurveyQuestionRPC.JSON_VERSION_NOT_SUPPORTED()`;
    };
    
    
    export interface InvalidQuestionDetailsJsonDTO {
      code: string;
    }
    
    export class InvalidQuestionDetailsJson extends LeoRPCError<RPCError> {
      readonly isInvalidQuestionDetailsJson: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidQuestionDetailsJson;
        super(code);
      }
    
    
      toDTO(): InvalidQuestionDetailsJsonDTO {
        const dto: InvalidQuestionDetailsJsonDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidQuestionDetailsJson.prototype.toString = function toString(): string {
      return `UpdateSurveyQuestionRPC.INVALID_QUESTION_DETAILS_JSON()`;
    };
    
    
    export interface InvalidChildQuestionTypeDTO {
      code: string;
    }
    
    export class InvalidChildQuestionType extends LeoRPCError<RPCError> {
      readonly isInvalidChildQuestionType: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidChildQuestionType;
        super(code);
      }
    
    
      toDTO(): InvalidChildQuestionTypeDTO {
        const dto: InvalidChildQuestionTypeDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidChildQuestionType.prototype.toString = function toString(): string {
      return `UpdateSurveyQuestionRPC.INVALID_CHILD_QUESTION_TYPE()`;
    };
    
    
    export interface InvalidMaxSelectionDTO {
      code: string;
    }
    
    export class InvalidMaxSelection extends LeoRPCError<RPCError> {
      readonly isInvalidMaxSelection: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidMaxSelection;
        super(code);
      }
    
    
      toDTO(): InvalidMaxSelectionDTO {
        const dto: InvalidMaxSelectionDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidMaxSelection.prototype.toString = function toString(): string {
      return `UpdateSurveyQuestionRPC.INVALID_MAX_SELECTION()`;
    };
    

    export type Errors = InvalidSurveyId | InvalidProjectId | InvalidQuestionId | QuestionWasDeleted | SurveyIsClosed | ProjectAlreadyArchived | QuestionCodeNotUnique | OptionCodeNotUnique | JsonVersionNotSupported | InvalidQuestionDetailsJson | InvalidChildQuestionType | InvalidMaxSelection;
    export type ErrorsDTO = InvalidSurveyIdDTO | InvalidProjectIdDTO | InvalidQuestionIdDTO | QuestionWasDeletedDTO | SurveyIsClosedDTO | ProjectAlreadyArchivedDTO | QuestionCodeNotUniqueDTO | OptionCodeNotUniqueDTO | JsonVersionNotSupportedDTO | InvalidQuestionDetailsJsonDTO | InvalidChildQuestionTypeDTO | InvalidMaxSelectionDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidSurveyId:
          return new InvalidSurveyId();
        case RPCError.InvalidProjectId:
          return new InvalidProjectId();
        case RPCError.InvalidQuestionId:
          return new InvalidQuestionId();
        case RPCError.QuestionWasDeleted:
          return new QuestionWasDeleted();
        case RPCError.SurveyIsClosed:
          return new SurveyIsClosed();
        case RPCError.ProjectAlreadyArchived:
          return new ProjectAlreadyArchived();
        case RPCError.QuestionCodeNotUnique:
          return new QuestionCodeNotUnique();
        case RPCError.OptionCodeNotUnique:
          return OptionCodeNotUnique.fromDTO(dto as OptionCodeNotUniqueDTO);
        case RPCError.JsonVersionNotSupported:
          return new JsonVersionNotSupported();
        case RPCError.InvalidQuestionDetailsJson:
          return new InvalidQuestionDetailsJson();
        case RPCError.InvalidChildQuestionType:
          return new InvalidChildQuestionType();
        case RPCError.InvalidMaxSelection:
          return new InvalidMaxSelection();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestSurveyIdError extends UpdateSurveyQuestionRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSurveyIdError";
  }
}

export class InvalidRequestQuestionIdError extends UpdateSurveyQuestionRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestQuestionIdError";
  }
}

export class InvalidRequestQuestionDetailsError extends UpdateSurveyQuestionRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestQuestionDetailsError";
  }
}

export class InvalidRequestProjectIdError extends UpdateSurveyQuestionRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectIdError";
  }
}

export class InvalidResponseSurveyDetailsAndQuestionsError extends UpdateSurveyQuestionRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyDetailsAndQuestionsError";
  }
}

export class InvalidResponseInvalidSurveyIdError extends UpdateSurveyQuestionRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidSurveyIdError";
  }
}

export class InvalidResponseInvalidProjectIdError extends UpdateSurveyQuestionRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidProjectIdError";
  }
}

export class InvalidResponseInvalidQuestionIdError extends UpdateSurveyQuestionRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidQuestionIdError";
  }
}

export class InvalidResponseQuestionWasDeletedError extends UpdateSurveyQuestionRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseQuestionWasDeletedError";
  }
}

export class InvalidResponseSurveyIsClosedError extends UpdateSurveyQuestionRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyIsClosedError";
  }
}

export class InvalidResponseProjectAlreadyArchivedError extends UpdateSurveyQuestionRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseProjectAlreadyArchivedError";
  }
}

export class InvalidResponseQuestionCodeNotUniqueError extends UpdateSurveyQuestionRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseQuestionCodeNotUniqueError";
  }
}

export class InvalidResponseOptionCodeNotUniqueError extends UpdateSurveyQuestionRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseOptionCodeNotUniqueError";
  }
}

export class InvalidResponseOptionCodeNotUniqueOptionCodeError extends InvalidResponseOptionCodeNotUniqueError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseOptionCodeNotUniqueOptionCodeError";
  }
}
export class InvalidResponseJsonVersionNotSupportedError extends UpdateSurveyQuestionRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseJsonVersionNotSupportedError";
  }
}

export class InvalidResponseInvalidQuestionDetailsJsonError extends UpdateSurveyQuestionRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidQuestionDetailsJsonError";
  }
}

export class InvalidResponseInvalidChildQuestionTypeError extends UpdateSurveyQuestionRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidChildQuestionTypeError";
  }
}

export class InvalidResponseInvalidMaxSelectionError extends UpdateSurveyQuestionRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidMaxSelectionError";
  }
}

